import { computed, ref } from 'vue';
import { track } from '@/helpers/tracker';
import i18next from '@/helpers/i18n.ts';
import { defineStore, acceptHMRUpdate } from 'pinia';
import * as Sentry from '@sentry/vue';
import api from '@/helpers/api';
import type { User } from '@cutr/constants/order';

// ignore user locale if ?lng param is set (developer debugging) or during e2e tests
const changeLanguageFromLocale = async (locale: string | undefined) => {
  if (!locale) return;
  if ((navigator.userAgent || '').startsWith('e2e-test')) return;
  const params = new URLSearchParams(window.location.search);
  if (params.get('lng')) return;
  await i18next.changeLanguage(locale);
};

const DEFAULT_USER: Partial<User> = {
  id: '',
  type: 'Public',
  email: '',
  organisationId: '',
  customerOrganisationId: '',
  linkedAccountUserId: '',
  sessionType: 'Public',
  phone: undefined,
};

const loadPersistedState = () => {
  try {
    const stored = localStorage.getItem('activeUser');
    if (stored) return JSON.parse(stored);
  } catch {
    // ignore; use default state
  }
  return null;
};

export const useActiveUserStore = defineStore('activeUser', () => {
  const persisted = loadPersistedState();

  const user = ref<Partial<User>>(persisted?.user ?? { ...DEFAULT_USER });
  const userSettings = ref<Record<string, boolean>>(
    persisted?.userSettings ?? {}
  );
  const userLoaded = ref(persisted?.userLoaded ?? false);
  const isFetchingPhoneNumber = ref(false);

  const activeUser = computed(() => user.value);
  const activeUserId = computed(() => user.value.id ?? '');
  const activeSessionType = computed(() => user.value.sessionType ?? 'Public');
  const activeSessionRole = computed(() => user.value.sessionRole);
  const superUserId = computed(() => user.value.superUserId);
  const activeUsername = computed(() => user.value.name || '');
  const activeUserPhoneNumber = computed(() => user.value.phone);
  const organisationId = computed(() => user.value.organisationId);
  const customerOrganisationId = computed(
    () => user.value.customerOrganisationId
  );
  const userCutlistAdminSourceId = computed(
    () => user.value.cutlistAdminSourceId
  );
  const activeUserSettings = computed(() => userSettings.value || {});
  const canSwitch = computed(
    () =>
      user.value.type === 'User' &&
      user.value.customerOrganisationId &&
      user.value.organisationId
  );
  const isAdmin = computed(() => activeSessionType.value === 'Admin');
  const isMaker = computed(() => activeSessionType.value === 'Maker');
  const isCustomer = computed(() => activeSessionType.value === 'Customer');
  const isLoggedIn = computed(() =>
    ['Admin', 'Maker', 'Customer'].includes(activeSessionType.value ?? '')
  );
  const isOrderCustomer = computed(() =>
    ['Customer', 'Unauthenticated'].includes(activeSessionType.value ?? '')
  );
  const isRoleOwner = computed(() => activeSessionRole.value === 'owner');

  const reset = () => {
    user.value = { ...DEFAULT_USER };
    userSettings.value = {};
    userLoaded.value = false;
    isFetchingPhoneNumber.value = false;
  };

  const setActiveUser = (newUser: User) => {
    userLoaded.value = true;
    user.value = newUser;
    userSettings.value = { viewerActive: true, visualDebug: false };

    Sentry.setUser(newUser);
    track('identify', newUser);

    fetchActiveUserPhoneNumber();

    if ((navigator.userAgent || '').startsWith('e2e-test')) {
      i18next.changeLanguage('en');
      return;
    }
    changeLanguageFromLocale(newUser.locale);
  };

  const updateActiveUser = async (newUser: Partial<User>) => {
    user.value = { ...user.value, ...newUser };
    await changeLanguageFromLocale(newUser.locale);
    fetchActiveUserPhoneNumber();
  };

  const setActiveUserToPublic = () => {
    user.value = { type: 'Public', sessionType: 'Public' };
  };

  const fetchActiveUserPhoneNumber = async () => {
    if (isFetchingPhoneNumber.value) return;

    try {
      isFetchingPhoneNumber.value = true;
      const response = await api.get(`/v2/users/${activeUserId.value}`);
      user.value.phone = response.data.data.phone;
    } catch (error) {
      console.error('Error fetching active user phone number:', error);
    } finally {
      isFetchingPhoneNumber.value = false;
    }
  };

  return {
    user,
    userSettings,
    userLoaded,
    isFetchingPhoneNumber,
    activeUser,
    activeUserId,
    activeSessionType,
    activeSessionRole,
    superUserId,
    activeUsername,
    activeUserPhoneNumber,
    organisationId,
    customerOrganisationId,
    userCutlistAdminSourceId,
    activeUserSettings,
    canSwitch,
    isAdmin,
    isMaker,
    isCustomer,
    isLoggedIn,
    isOrderCustomer,
    isRoleOwner,
    reset,
    setActiveUser,
    updateActiveUser,
    setActiveUserToPublic,
    fetchActiveUserPhoneNumber,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useActiveUserStore, import.meta.hot));
}
